import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { LiveTileContent, TileScrollableHandle } from "../LiveTileContent";
import { AppImageDisplayInfo } from "client/Api/AppIcon/common";
import { navigateTo } from "client_legacy/ndaw/navigator";
import { getHubUrlForCommunity } from "client/Features/Hub/Communities/CommunitiesPage";
import { HubDataSource } from "client/Api/Hub/hub";
import { CommunityListItem } from "client/Api/Workspace/LiveInformation/Hub/communityLiveInformation";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	AlertManager,
	useAlerts,
	useForceRerender,
	useStableImperativeCallback,
	useUnmountedCancellation
} from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import { Guid, newGuid } from "client_legacy/ndaw/guid";
import { TileTabProps } from "../TileTabSelector";
import { isNil, isNotNil } from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { CommunityFeed } from "client/Features/Hub/Communities/CommunityFeed";
import { useCommunityPostDialog } from "client/Features/Hub/Communities/CommunityPostDialog";
import {
	FeedPageModel,
	getFeedPageInfo,
	listNewCommunities,
	NewCommunityItemViewModel
} from "client/Api/Communities/communityFeed";
import { BusyIndicator } from "client/Components";
import { never } from "client_legacy/ndaw/errors";
import styles from "./FeedView.module.less";
import { CommunityLiveInformationProvider } from "client_legacy/Workspace/LiveInformation/Hub/communityProvider";
import { HubEvent, publish } from "client/Features/Hub/events";
import { markCommunityAsSeen } from "client/Api/Communities/community";
import { NewCommunities } from "./NewCommunities";
import { DimensionToRender } from "../../RenderState/common";

export type FeedViewProps = {
	icon: AppImageDisplayInfo;
	hideHeader: boolean;
	hideFooter: boolean;
	tileId: number;
	tileName: string;
	onAddAnalyticsEvent: () => void;
	communityProvider: CommunityLiveInformationProvider;
	dimension: DimensionToRender;
};

export const FeedView = (props: FeedViewProps): ReactElement => {
	const {
		hideFooter,
		hideHeader,
		icon,
		onAddAnalyticsEvent,
		tileId,
		tileName,
		communityProvider,
		dimension
	} = props;

	const [activeTabId, setActiveTabId] = useState<Guid>(MY_FEED_TAB.uniqueId);
	const [tabsState, setTabsState] = useState<TabsState>({ status: "loading"});
	const [reload, dataVersion] = useForceRerender();;
	const alertManager: AlertManager = useAlerts();

	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const activeTab = useMemo(
		(): CommunityTab | undefined => {
			if (tabsState.status !== "loaded") {
				return undefined;
			}

			return tabsState.tabs
				.find(x => x.uniqueId === activeTabId);
		},
		[activeTabId, tabsState]
	);

	useEffect(
		(): void | (() => void) => {
			hasImplicitDependencyOn(dataVersion);

			async function fetchItems(): Promise<void> {
				setTabsState({ status: "loading" });

				try {
					const communities: readonly CommunityListItem[] = await communityProvider.listCommunities();
					const tabs: readonly CommunityTab[] = communities.map(
						(x: CommunityListItem): CommunityTab => {
							return {
								communityId: x.id,
								name: x.name,
								uniqueId: newGuid()
							};
						}
					);

					setTabsState({
						status: "loaded",
						tabs: [
							MY_FEED_TAB,
							...tabs
						]
					});
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					setTabsState({ status: "error" });
				}
			}

			fetchItems();
		},
		[
			alertManager,
			dataVersion,
			communityProvider
		]
	);

	const clearCommunityNewStatusHandler = useCallback(
		async (communityId: number, signal: AbortSignal): Promise<void> => {
			publish(
				HubEvent.IsNewStatusCleared,
				{
					kind: HubDataSource.Community,
					communityId
				}
			);

			try {
				await markCommunityAsSeen(
					communityId,
					signal
				);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
			}
		},
		[]
	);

	const navigateToCommunityHandler = useCallback(
		(communityId: number | "my-feed"): void => {
			onAddAnalyticsEvent();

			if (communityId !== "my-feed") {
				clearCommunityNewStatusHandler(communityId, globalCancellation);
			}

			const url: string = getHubUrlForCommunity({
				source: HubDataSource.Community,
				communityId
			});

			navigateTo(url);
		},
		[
			clearCommunityNewStatusHandler,
			globalCancellation,
			onAddAnalyticsEvent
		]
	);

	const navigateToActiveCommunityHandler = useCallback(
		(): void => {
			if (isNil(activeTab)) {
				return;
			}

			navigateToCommunityHandler(activeTab.communityId);
		},
		[activeTab, navigateToCommunityHandler]
	);

	const selectCommunityHandler = useCallback(
		(communityId: number): void => {
			if (tabsState.status !== "loaded") {
				return;
			}

			const selectedCommunity: CommunityTab | undefined = tabsState.tabs
				.find(x => x.communityId === communityId);

			if (isNil(selectedCommunity)) {
				return;
			}

			clearCommunityNewStatusHandler(communityId, globalCancellation);

			setActiveTabId(selectedCommunity.uniqueId);
		},
		[
			tabsState,
			clearCommunityNewStatusHandler,
			globalCancellation
		]
	);

	const activeTabChangeHandler = useCallback(
		(tabId: Guid): void => {
			setActiveTabId(tabId);

			if (tabsState.status !== "loaded") {
				return;
			}

			const selectedCommunity: CommunityTab | undefined = tabsState.tabs
				.find(x => x.uniqueId === tabId);

			if (isNil(selectedCommunity) || selectedCommunity.communityId === "my-feed") {
				return;
			}

			clearCommunityNewStatusHandler(selectedCommunity.communityId, globalCancellation);
		},
		[
			tabsState,
			clearCommunityNewStatusHandler,
			globalCancellation
		]
	);

	const renderContent = (): ReactElement => {
		switch (tabsState.status) {
			case "loading":
				return (
					<BusyIndicator />
				);
			case "error":
				return (
					<LiveTileContent.NoResults
						icon="gear"
						title={localize("_UnableToRetrieveItems")}
						message={localize("_WeWereUnableToRetrieveTheItems")}
						onRetry={reload}
					/>
				);
			case "loaded":
				return (
					<>
						{isNotNil(activeTab) && (
							<CommunityLiveFeedContent
								id={activeTab.communityId}
								onAddAnalyticsEvent={onAddAnalyticsEvent}
								onSelectCommunity={selectCommunityHandler}
								onNavigateToCommunity={navigateToCommunityHandler}
								onClearCommunityNewStatus={clearCommunityNewStatusHandler}
								dimension={dimension}
							/>
						)}
					</>
				);
			default:
				never(tabsState);
		}
	};

	return (
		<>
			{!hideHeader && (
				<LiveTileContent.Header icon={icon}>
					{tabsState.status === "loaded" && (
						<LiveTileContent.TabSelector
							value={activeTabId}
							tabs={tabsState.tabs}
							onChange={activeTabChangeHandler}
							tileId={tileId}
							tileName={tileName}
						/>
					)}
				</LiveTileContent.Header>
			)}
			<LiveTileContent.Content>
				<LiveTileContent.ContentStack>
					<LiveTileContent.BusyIndicatorContainer>
						{renderContent()}
					</LiveTileContent.BusyIndicatorContainer>
				</LiveTileContent.ContentStack>
			</LiveTileContent.Content>

			{!hideFooter && isNotNil(activeTab) && (
				<LiveTileContent.Footer
					name={activeTab.name}
					onClick={navigateToActiveCommunityHandler}
				/>
			)}
		</>
	);
};
FeedView.displayName="FeedView";

const MY_FEED_TAB: CommunityTab = {
	communityId: "my-feed",
	name: localize("_MyFeed"),
	uniqueId: newGuid()
};

type TabsState = {
	status: "loading" | "error";
} | {
	status: "loaded";
	tabs: readonly CommunityTab[];
};

type CommunityTab = TileTabProps & {
	communityId: number | "my-feed";
};

type CommunityLiveFeedContentProps = {
	id: number | "my-feed";
	onAddAnalyticsEvent: () => void;
	onNavigateToCommunity: (communityId: number) => void;
	onSelectCommunity: (communityId: number) => void;
	onClearCommunityNewStatus: (communityId: number, signal: AbortSignal) => Promise<void>;
	dimension: DimensionToRender;
};

const CommunityLiveFeedContent = (props: CommunityLiveFeedContentProps): ReactElement => {
	const {
		id,
		onAddAnalyticsEvent,
		onNavigateToCommunity,
		onSelectCommunity,
		onClearCommunityNewStatus,
		dimension
	} = props;

	const [feedPageInfo, setFeedPageInfo] = useState<FeedPageInfoLoadingState>({ kind: "loading" });
	const [newCommunitiesState, setNewCommunitiesState] = useState<NewCommunitiesLoadingState>({ kind: "loading" });

	const [communityPostDialog, openCommunityPostDialog] = useCommunityPostDialog();
	const [onReloadFeedPageInfo, feedPageInfoVersion] = useForceRerender();
	const alertManager: AlertManager = useAlerts();
	const scrollableRef: RefObject<TileScrollableHandle | null> = useRef<TileScrollableHandle>(null);

	const stableOnClearCommunityNewStatus: typeof onClearCommunityNewStatus = useStableImperativeCallback(onClearCommunityNewStatus);

	useEffect(
		(): (() => void) => {
			hasImplicitDependencyOn(feedPageInfoVersion);
			const load = async (signal: AbortSignal): Promise<void> => {
				setFeedPageInfo({ kind: "loading"});

				try {
					const {
						communitiesInFeed,
						currentCommunity,
						options
					} = await getFeedPageInfo(id, signal);

					if (isNotNil(currentCommunity)) {
						stableOnClearCommunityNewStatus(currentCommunity.id, signal);
					}

					setFeedPageInfo({
						kind: "loaded",
						communitiesInFeed,
						options,
						currentCommunity
					});
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					setFeedPageInfo({ kind: "error" });

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			};

			const cancellation = new AbortController();
			load(cancellation.signal);
			return (): void => cancellation.abort();
		},
		[
			id,
			alertManager,
			feedPageInfoVersion,
			stableOnClearCommunityNewStatus
		]
	);

	useEffect(
		(): (() => void) | void => {
			const { columnSpan, rowSpan } = dimension.dimension;

			const shouldFetchNewCommunities: boolean = columnSpan >= 8
				&& rowSpan >= 8;

			if (!shouldFetchNewCommunities) {
				setNewCommunitiesState({
					kind: "loaded",
					result: []
				});
				return;
			}

			async function fetchNewCommunities(signal: AbortSignal): Promise<void> {
				setNewCommunitiesState({ kind: "loading" });

				try {
					const result: readonly NewCommunityItemViewModel[] = await listNewCommunities(signal);
					setNewCommunitiesState({
						kind: "loaded",
						result
					});
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e, "listing new communities failed");
					setNewCommunitiesState({ kind: "error" });
				}
			}

			const cancellation = new AbortController();
			fetchNewCommunities(cancellation.signal);
			return (): void => cancellation.abort();
		},
		[dimension.dimension]
	);

	const openCommunityPostDialogHandler = useCallback(
		(postId: number, deeplink?: "comments"): void => {
			onAddAnalyticsEvent();

			openCommunityPostDialog({
				id: postId,
				deepLink: deeplink === "comments"
					? { kind: "comments" }
					: undefined
			});
		},
		[onAddAnalyticsEvent, openCommunityPostDialog]
	);

	const scrollToTopHandler = useCallback(
		(): void => {
			scrollableRef.current
				?.scrollTo(0);
		},
		[]
	);

	const renderContent = (): ReactElement => {
		if (newCommunitiesState.kind === "loading" || feedPageInfo.kind === "loading") {
			return <BusyIndicator />;
		}

		switch (feedPageInfo.kind) {
			case "error":
				return (
					<LiveTileContent.NoResults
						icon="gear"
						title={localize("_UnableToRetrieveItems")}
						message={localize("_WeWereUnableToRetrieveTheItems")}
						onRetry={onReloadFeedPageInfo}
					/>
				);
			case "loaded":
				const newCommunities: readonly NewCommunityItemViewModel[] = newCommunitiesState.kind === "loaded"
					? newCommunitiesState.result
					: [];

				return (
					<LiveTileContent.Scrollable ref={scrollableRef}>
						<div className={styles.feedContainer}>
							{newCommunities.length > 0 && (
								<NewCommunities
									communities={newCommunities}
									onAddAnalyticsEvent={onAddAnalyticsEvent}
									onNavigateToCommunity={onNavigateToCommunity}
								/>
							)}
							<div className={styles.posts}>
								<CommunityFeed
									id={id}
									onOpenPost={openCommunityPostDialogHandler}
									communitiesInFeed={feedPageInfo.communitiesInFeed}
									options={feedPageInfo.options}
									mobileCommentHostingLocation="page"
									onNewPost={scrollToTopHandler}
									onNavigateToCommunity={onSelectCommunity}
								/>
							</div>
						</div>
					</LiveTileContent.Scrollable>
				);
			default:
				never(feedPageInfo.kind);
		}
	};

	return (
		<>
			{renderContent()}
			{communityPostDialog}
		</>
	);
};
CommunityLiveFeedContent.displayName="CommunityLiveFeedContent";

type FeedPageInfoLoadingState = {
	kind: "error" | "loading";
} | (
	{
		kind: "loaded";
	} & FeedPageModel
);

type NewCommunitiesLoadingState = {
	kind: "loading" | "error";
} | {
	kind: "loaded";
	result: readonly NewCommunityItemViewModel[];
};